<template>
  <AdminLayout page-title="Article Management">
    <template #header-actions>
      <button
        @click="router.push('/admin/articles/new')"
        class="dashboard-primary-btn flex items-center gap-2 text-sm"
        aria-label="Create new article"
      >
        <svg
          class="w-5 h-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
        <span class="hidden sm:inline">New Article</span>
      </button>
    </template>

    <div class="mx-auto">
      <div
        class="dashboard-card bg-surface rounded-xl border border-outline-variant/30 overflow-hidden"
      >
        <div class="hidden md:block overflow-x-auto">
          <table class="w-full">
            <thead class="bg-surface-container-low">
              <tr>
                <th
                  class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider"
                >
                  Title
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider"
                >
                  Category
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider"
                >
                  Author
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider"
                >
                  Date
                </th>
                <th
                  class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider"
                >
                  Status
                </th>
                <th
                  class="px-6 py-4 text-right text-xs font-semibold text-on-surface uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/30">
              <tr
                v-for="article in articlesList"
                :key="article.id"
                class="hover:bg-surface-container-low transition-colors"
              >
                <td class="px-6 py-4">
                  <div class="text-sm font-medium text-on-surface">
                    {{ article.title }}
                  </div>
                  <div class="text-xs text-on-surface-variant mt-1 line-clamp-1">
                    {{ article.excerpt }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full"
                  >
                    {{ article.category }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                  {{ article.author }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                  {{ article.date }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="px-3 py-1 text-xs font-medium rounded-full"
                    :class="
                      article.is_published
                        ? 'bg-primary/10 text-primary'
                        : 'bg-surface-container-low text-on-surface-variant'
                    "
                  >
                    {{ article.is_published ? 'Published' : 'Draft' }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div class="flex items-center justify-end gap-2">
                    <a
                      v-if="article.is_published"
                      :href="publicArticleUrl(article)"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="text-primary hover:text-primary-container transition-colors"
                      title="View public article"
                      aria-label="View public article"
                    >
                      <svg
                        class="w-5 h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                    <button
                      @click="router.push(`/admin/articles/${article.id}/edit`)"
                      class="text-primary hover:text-primary-container transition-colors cursor-pointer"
                      title="Edit"
                      aria-label="Edit article"
                    >
                      <svg
                        class="w-5 h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      @click="deleteArticle(article.id)"
                      class="text-error hover:text-error/80 transition-colors cursor-pointer"
                      title="Delete"
                      aria-label="Delete article"
                    >
                      <svg
                        class="w-5 h-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="articlesList.length === 0">
                <td colspan="6" class="px-6 py-12 text-center text-on-surface-variant">
                  No articles found. Create your first article!
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="md:hidden divide-y divide-outline-variant/30">
          <div
            v-for="article in articlesList"
            :key="article.id"
            class="p-4 hover:bg-surface-container-low transition-colors"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="flex-1 min-w-0">
                <h3 class="text-sm font-medium text-on-surface truncate">
                  {{ article.title }}
                </h3>
                <p class="text-xs text-on-surface-variant mt-1 line-clamp-2">
                  {{ article.excerpt }}
                </p>
                <div class="flex items-center gap-3 mt-2 flex-wrap">
                  <span
                    class="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full"
                  >
                    {{ article.category }}
                  </span>
                  <span class="text-xs text-on-surface-variant">
                    {{ article.author }}
                  </span>
                  <span class="text-xs text-on-surface-variant">
                    {{ article.date }}
                  </span>
                  <span
                    class="px-2 py-1 text-xs font-medium rounded-full"
                    :class="
                      article.is_published
                        ? 'bg-primary/10 text-primary'
                        : 'bg-surface-container-low text-on-surface-variant'
                    "
                  >
                    {{ article.is_published ? 'Published' : 'Draft' }}
                  </span>
                </div>
              </div>
              <div class="flex items-center gap-2 flex-shrink-0">
                <a
                  v-if="article.is_published"
                  :href="publicArticleUrl(article)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary hover:text-primary-container transition-colors"
                  title="View public article"
                  aria-label="View public article"
                >
                  <svg
                    class="w-5 h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
                <button
                  @click="router.push(`/admin/articles/${article.id}/edit`)"
                  class="text-primary hover:text-primary-container transition-colors cursor-pointer"
                  title="Edit"
                  aria-label="Edit article"
                >
                  <svg
                    class="w-5 h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
                <button
                  @click="deleteArticle(article.id)"
                  class="text-error hover:text-error/80 transition-colors cursor-pointer"
                  title="Delete"
                  aria-label="Delete article"
                >
                  <svg
                    class="w-5 h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div
            v-if="articlesList.length === 0"
            class="px-6 py-12 text-center text-on-surface-variant"
          >
            No articles found. Create your first article!
          </div>
        </div>
      </div>
    </div>

    <ConfirmModal
      :open="deleteModalOpen"
      title="Delete article"
      message="Are you sure you want to delete this article? This action cannot be undone."
      confirm-label="Delete"
      cancel-label="Cancel"
      variant="danger"
      @confirm="confirmDelete"
      @cancel="closeDeleteModal"
    />
  </AdminLayout>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { authService } from "../../utils/auth.js";
import {
  getAllArticlesAdmin,
  deleteArticle as deleteArticleFromDb,
} from "../../utils/articles.js";
import AdminLayout from "../../components/admin/AdminLayout.vue";
import ConfirmModal from "../../components/admin/ConfirmModal.vue";
import { showAdminToast } from "../../utils/adminToast.js";

const router = useRouter();

const publicArticleUrl = (article) =>
  `/article/${article.slug || article.id}`;

const articlesList = ref([]);
const isLoading = ref(false);
const deleteModalOpen = ref(false);
const pendingDeleteId = ref(null);

const loadArticles = async () => {
  isLoading.value = true;
  try {
    articlesList.value = await getAllArticlesAdmin();
  } catch (error) {
    console.error("Error loading articles:", error);
    showAdminToast("Error loading articles", "error");
  } finally {
    isLoading.value = false;
  }
};

const deleteArticle = (articleId) => {
  pendingDeleteId.value = articleId;
  deleteModalOpen.value = true;
};

const closeDeleteModal = () => {
  deleteModalOpen.value = false;
  pendingDeleteId.value = null;
};

const confirmDelete = async () => {
  const articleId = pendingDeleteId.value;
  closeDeleteModal();
  if (!articleId) return;

  try {
    await deleteArticleFromDb(articleId);
    showAdminToast("Article deleted successfully!", "success");
    await loadArticles();
  } catch (error) {
    console.error("Error deleting article:", error);
    showAdminToast("Error deleting article. Please try again.", "error");
  }
};

onMounted(async () => {
  if (!authService.isAuthenticated()) {
    router.push("/admin/login");
    return;
  }

  await loadArticles();
});
</script>

<style scoped>
.dashboard-card {
  box-shadow: 0 10px 30px rgba(21, 66, 18, 0.08);
}

.dashboard-primary-btn {
  padding: 0.5rem 1.25rem;
  border-radius: 9999px;
  background-color: #154212;
  color: #a9f862;
  font-weight: 600;
  border: none;
  box-shadow: 0 4px 10px rgba(21, 66, 18, 0.25);
  transition: background-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.1s ease;
}

.dashboard-primary-btn:hover {
  background-color: #2d5a27;
  box-shadow: 0 6px 16px rgba(21, 66, 18, 0.3);
  transform: translateY(-1px);
}

.dashboard-primary-btn:active {
  transform: translateY(0);
  box-shadow: 0 3px 8px rgba(21, 66, 18, 0.2);
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
